import { uiText, useUiLanguage } from '@/i18n/interface';

import React from 'react';
import { useTranslation } from 'react-i18next';
import PaymentCategoryManager from './PaymentCategoryManager';
import ExpenseManager from './ExpenseManager';
import { PaymentCategory } from '@/types/payment';
import { useToast } from "@/hooks/use-toast";
import { supabase } from '@/integrations/supabase/client';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from '@/contexts/AuthContext';
import { useDojoPaymentMethods } from '@/hooks/useDojoPaymentMethods';
import { useEffectiveDojo } from '@/hooks/useEffectiveDojo';

interface PaymentMethod {
  id: string;
  name: string;
  label: string;
}

interface PaymentSettings {
  defaultCurrency: string;
  availableCurrencies: string[];
  discountPercentage: number;
  availablePaymentMethods: PaymentMethod[];
}

interface PaymentCategoriesTabProps {
  categories: PaymentCategory[];
  onUpdateCategory: (categoryId: string, field: string, value: any) => void;
  onCategoriesChange?: (categories: PaymentCategory[]) => void;
}

const PaymentCategoriesTab: React.FC<PaymentCategoriesTabProps> = ({
  categories,
  onUpdateCategory,
  onCategoriesChange
}) => {
  useUiLanguage();
  const { t } = useTranslation('payments');
  const { toast } = useToast();
  const { profile } = useAuth();
  const { availablePaymentMethods, defaultCurrency } = useDojoPaymentMethods();
  const { effectiveDojoId } = useEffectiveDojo();

  // Dynamic settings based on actual dojo configuration
  const settings: PaymentSettings = {
    defaultCurrency,
    availableCurrencies: ['USD', 'CAD', 'EUR', 'GBP', 'AUD', 'CHF'],
    discountPercentage: 0,
    availablePaymentMethods
  };

  const handleAddCategory = async (category: Omit<PaymentCategory, 'id'>) => {
    try {
        const { data, error } = await supabase
          .from('payment_categories')
          .insert({
            name: category.name,
            one_year_amount: Math.round(category.amounts.oneYear * 100), // Convert to cents
            six_months_amount: Math.round(category.amounts.sixMonths * 100), // Convert to cents
            three_months_amount: Math.round(category.amounts.threeMonths * 100), // Convert to cents
            monthly_amount: Math.round(category.amounts.monthly * 100), // Convert to cents
            currency: category.currency || 'USD',
            payment_methods: category.paymentMethods || ['bank_transfer'],
            is_active: category.isActive !== false,
            stripe_integration_enabled: category.stripeIntegrationEnabled || false,
            auto_generate_links: category.autoGenerateLinks || false,
            dojo_id: effectiveDojoId!
          })
          .select()
          .single();

      if (error) throw error;

      toast({
        title: uiText("Success"),
        description: uiText("Payment category added successfully")
      });

      if (onCategoriesChange) {
        const newCategory: PaymentCategory = {
          id: data.id,
          name: data.name,
          amounts: {
            oneYear: (data.one_year_amount || 0) / 100,
            sixMonths: (data.six_months_amount || 0) / 100,
            threeMonths: (data.three_months_amount || 0) / 100,
            monthly: (data.monthly_amount || 0) / 100
          },
          currency: data.currency || 'USD',
          paymentMethods: Array.isArray(data.payment_methods)
            ? data.payment_methods as string[]
            : ['bank_transfer'],
          isActive: data.is_active !== false,
          stripeIntegrationEnabled: data.stripe_integration_enabled || false,
          stripePriceIds: {},
          paymentLinks: {},
          autoGenerateLinks: data.auto_generate_links || false
        };
        onCategoriesChange([...categories, newCategory]);
      }
    } catch (error) {
      console.error('Error adding category:', error);
      toast({
        title: uiText("Error"),
        description: uiText("Failed to add payment category"),
        variant: "destructive"
      });
    }
  };

  const handleRemoveCategory = async (categoryId: string) => {
    try {
      const { error } = await supabase
        .from('payment_categories')
        .delete()
        .eq('id', categoryId);

      if (error) throw error;

      toast({
        title: uiText("Success"),
        description: uiText("Payment category deleted successfully")
      });

      // Refresh the categories from database to ensure UI reflects actual state
      if (onCategoriesChange) {
        // Re-fetch all categories instead of filtering local state
        const { data: updatedCategories } = await supabase
          .from('payment_categories')
          .select('*')
          .eq('dojo_id', effectiveDojoId)
          .order('name');

        if (updatedCategories) {
          const formattedCategories = updatedCategories.map(cat => ({
            id: cat.id,
            name: cat.name,
            amounts: {
              oneYear: (cat.one_year_amount || 0) / 100,
              sixMonths: (cat.six_months_amount || 0) / 100,
              threeMonths: (cat.three_months_amount || 0) / 100,
              monthly: (cat.monthly_amount || 0) / 100
            },
            currency: cat.currency || 'USD',
            paymentMethods: Array.isArray(cat.payment_methods)
              ? cat.payment_methods as string[]
              : ['bank_transfer'],
            isActive: cat.is_active !== false,
            stripeIntegrationEnabled: cat.stripe_integration_enabled || false,
            stripePriceIds: (cat.stripe_price_ids && typeof cat.stripe_price_ids === 'object' && !Array.isArray(cat.stripe_price_ids))
              ? cat.stripe_price_ids as Record<string, string>
              : {},
            paymentLinks: (cat.payment_links && typeof cat.payment_links === 'object' && !Array.isArray(cat.payment_links))
              ? cat.payment_links as Record<string, string>
              : {},
            autoGenerateLinks: cat.auto_generate_links || false
          }));
          onCategoriesChange(formattedCategories);
        }
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      toast({
        title: uiText("Error"),
        description: uiText("Failed to delete payment category"),
        variant: "destructive"
      });
    }
  };

  const handleUpdateSettings = (newSettings: Partial<PaymentSettings>) => {
    // This would typically be handled by the parent component
    console.log('Update settings:', newSettings);
  };

  // Filter categories to only show subscription categories (not expense categories)
  const subscriptionCategories = categories.filter(cat =>
    !['Office Supplies', 'Equipment', 'Utilities', 'Marketing'].includes(cat.name)
  );

  return (
    <div className="w-full">
      <Tabs defaultValue="categories" className="w-full">
        <TabsList className="grid w-full grid-cols-1 sm:grid-cols-2 h-auto sm:h-10 gap-1 sm:gap-0">
          <TabsTrigger value="categories" className="text-xs sm:text-sm p-2 sm:p-3">
            <span className="hidden sm:inline">{t("payments:categories")}</span>
            <span className="sm:hidden">{t("payments:categories")}</span>
          </TabsTrigger>
          <TabsTrigger value="expenses" className="text-xs sm:text-sm p-2 sm:p-3">{t('common:admin.expenses')}</TabsTrigger>
        </TabsList>

        <TabsContent value="categories">
          <PaymentCategoryManager
            categories={subscriptionCategories}
            onUpdateCategory={onUpdateCategory}
            onAddCategory={handleAddCategory}
            onRemoveCategory={handleRemoveCategory}
            onUpdateSettings={handleUpdateSettings}
            settings={settings}
          />
        </TabsContent>

        <TabsContent value="expenses">
          <ExpenseManager categories={categories} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PaymentCategoriesTab;
